import React, { useCallback, useEffect, useReducer, useState } from "react";
import { IconSearchDouble, TabPane } from "@americanbible/uikit";

import { SearchBar, SearchInput, StyledCard, StyledTabs } from "./styles";

export const TabSelectMenuPane = TabPane;

const defaultTabSelectMenuState = {
  resizeHeight: false,
  search: "",
  activeSearchItemIndex: 0
};

function tabSelectMenuReducer(state, action) {
  switch (action.type) {
    case "search":
      const search = action.payload;
      return {
        ...state,
        resizeHeight: !state.resizeHeight,
        search,
        activeSearchItemIndex: 0 // search === "" ? null : 0
      };
    case "resize":
      return {
        ...state,
        resizeHeight: !state.resizeHeight
      };
    case "up-arrow":
    case "left-arrow":
      return {
        ...state,
        activeSearchItemIndex: state.activeSearchItemIndex - 1
      };
    case "down-arrow":
    case "right-arrow":
      return {
        ...state,
        activeSearchItemIndex: state.activeSearchItemIndex + 1
      };
    case "reset":
      return {
        ...defaultTabSelectMenuState,
        resizeHeight: !state.resizeHeight,
        activeSearchItemIndex: action.payload.activeSearchItemIndex
      };
    default:
      throw new Error("invalid action type for tabSelectMenuReducer");
  }
}

const matchRegexOperatorsRegex = /[|\\{}()[\]^$+*?.-]/g;

function filterSearchValues(menuItems, search) {
  const escapedSearch = search.replace(matchRegexOperatorsRegex, "\\$&");
  const searchRegExp = new RegExp(`^${escapedSearch}|\\s${escapedSearch}`, "i");
  return menuItems.filter(({ searchValues }) =>
    searchValues.some(value => searchRegExp.test(value))
  );
}

// allows negative numbers to wrap around the back of the list
const findActiveRowIndex = (activeSearchItemIndex, menuItemLength) =>
  ((activeSearchItemIndex % menuItemLength) + menuItemLength) % menuItemLength;

export const TabSelectMenu = ({
  menus,
  activeMenuKey,
  onTabClick,
  ...props
}) => {
  const [activeTabKey, setActiveTabKey] = useState(
    activeMenuKey || menus[0].key
  );

  useEffect(() => dispatch({ type: "resize" }), [activeTabKey]);
  useEffect(() => activeMenuKey && setActiveTabKey(activeMenuKey), [
    activeMenuKey
  ]);

  const [state, dispatch] = useReducer(tabSelectMenuReducer, {
    ...defaultTabSelectMenuState,
    activeSearchItemIndex:
      menus.find(({ key }) => key === activeTabKey).activeMenuItemIndex || 0
  });
  const setFocus = useCallback(
    node => activeTabKey && node !== null && node.focus(),
    [activeTabKey]
  );

  const handleSearch = e =>
    dispatch({ type: "search", payload: e.target.value });

  const handleTabClick = newActiveTabKey => {
    if (onTabClick) {
      onTabClick(newActiveTabKey);
    } else {
      setActiveTabKey(newActiveTabKey);
    }

    dispatch({
      type: "reset",
      payload: {
        activeSearchItemIndex:
          menus.find(({ key }) => key === newActiveTabKey)
            .activeMenuItemIndex || 0
      }
    });
  };

  // filter our menu items of current tab if there is a search value
  menus = menus.map((menu, index) => ({
    ...menu,
    menuItems:
      menu.key === activeTabKey && state.search !== ""
        ? filterSearchValues(menu.menuItems, state.search)
        : menu.menuItems
  }));

  const menuTabPanes = menus.map(
    (
      {
        tab,
        key,
        menuItems,
        activeMenuItemIndex,
        tabPaneComponent: TabPaneComponent
      },
      index
    ) => {
      return (
        <TabPane tab={tab} key={key}>
          <TabPaneComponent
            menuItems={menuItems.map(menuItem => ({
              ...menuItem,
              onSelect: () => {
                dispatch({ type: "search", payload: "" });
                menuItem.onSelect();
              }
            }))}
            defaultActiveRowIndex={activeMenuItemIndex}
            activeRowIndex={findActiveRowIndex(
              state.activeSearchItemIndex,
              menuItems.length
            )}
            searchValue={state.search}
          />
        </TabPane>
      );
    }
  );

  const handleNavigation = e => {
    if (e.keyCode === 37) return dispatch({ type: "left-arrow" });
    if (e.keyCode === 39) return dispatch({ type: "right-arrow" });
    if (e.keyCode === 38) {
      e.preventDefault(); // keeps cursor from moving to start of inpupt
      return dispatch({ type: "up-arrow" });
    }
    if (e.keyCode === 40) return dispatch({ type: "down-arrow" });
    if (e.keyCode === 13) {
      const currentMenuItems = menus.find(menu => menu.key === activeTabKey)
        .menuItems;
      currentMenuItems[
        state.activeSearchItemIndex % currentMenuItems.length
      ].onSelect();
      dispatch({ type: "search", payload: "" });
    }
  };

  return (
    <StyledCard {...props}>
      <StyledTabs
        onTabClick={handleTabClick}
        activeKey={activeTabKey}
        resizeHeight={state.resizeHeight}
        prependTabContent={
          <SearchBar>
            <IconSearchDouble />
            <SearchInput
              ref={setFocus}
              type="text"
              value={state.search}
              onChange={handleSearch}
              onKeyDown={handleNavigation}
            />
          </SearchBar>
        }
      >
        {menuTabPanes}
      </StyledTabs>
    </StyledCard>
  );
};
